import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import ProductCard from '../components/ProductCard';
import { products } from '../data/products';
import { useLanguage } from '../context/LanguageContext';
import './Home.css';

const Home = () => {
  const { t } = useLanguage();

  const categoryCards = useMemo(
    () => [
      {
        title: t('categories.hazelnuts.title'),
        subtitle: t('categories.hazelnuts.subtitle'),
        target: 'hazelnuts-section',
        image: '/images/products/hazelnuts/jute-10kg.png'
      },
      {
        title: t('categories.truffles.title'),
        subtitle: t('categories.truffles.subtitle'),
        target: 'truffles-section',
        image: '/images/products/truffles/truffle.png'
      },
      {
        title: t('categories.snaps.title'),
        subtitle: t('categories.snaps.subtitle'),
        target: 'snacks-section',
        image: '/images/products/snaps/snaps_500.png'
      }
    ],
    [t]
  );

  const sections = useMemo(
    () => [
      {
        id: 'hazelnuts-section',
        layout: 'grid',
        title: t('sections.hazelnuts.title'),
        description: t('sections.hazelnuts.description'),
        items: products.filter((p) => p.category === 'hazelnuts')
      },
      {
        id: 'truffles-section',
        layout: 'grid',
        preview: true,
        title: t('sections.truffles.title'),
        description: t('sections.truffles.description'),
        items: products.filter((p) => p.category === 'truffles')
      },
      {
        id: 'snacks-section',
        layout: 'grid',
        preview: true,
        title: t('sections.snacks.title'),
        description: t('sections.snacks.description'),
        items: products.filter((p) => p.category === 'snacks')
      }
    ],
    [t]
  );

  const scrollToSection = (target) => {
    const element = document.getElementById(target);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCardKeyDown = (event, target) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      scrollToSection(target);
    }
  };

  const renderProductSection = (section) => {
    if (section.layout === 'split') {
      return (
        <section className="product-section product-section--split" id={section.id} key={section.id}>
          <div className="container">
            <div className="section-split">
              <div className="section-split-copy">
                <h2 className="section-title section-title--left">{section.title}</h2>
                <p className="section-description section-description--left">{section.description}</p>
              </div>
              <div className="products-grid products-grid--compact">
                {section.items.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        className={`product-section${section.preview ? ' product-section--preview' : ''}`}
        id={section.id}
        key={section.id}
      >
        <div className="container">
          <h2 className="section-title">{section.title}</h2>
          <p className="section-description">{section.description}</p>
          <div className={`products-grid${section.preview ? ' products-grid--preview' : ''}`}>
            {section.items.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="home">
      <Hero />

      <section className="about-section" id="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="about-title">{t('about.title')}</h2>
              <p>{t('about.paragraph1')}</p>
              <p>{t('about.paragraph2')}</p>
              {Array.isArray(t('about.highlights')) && (
                <ul className="about-highlights">
                  {t('about.highlights').map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              <div className="about-actions">
                <button
                  type="button"
                  className="about-btn btn-primary"
                  onClick={() => scrollToSection('hazelnuts-section')}
                >
                  {t('about.button')}
                </button>
                <Link to="/about" className="about-timeline-link">
                  {t('about.timelineButton')}
                </Link>
              </div>
            </div>
            <div className="about-image">
              <img
                src="/images/timeline/2026/2026-05-23_56EFE65E-77AA-4C5A-9249-A557EA4F21E2.jpg"
                alt={t('common.harvestImageAlt')}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="categories-section">
        <div className="container">
          <p className="section-eyebrow">{t('categories.title')}</p>
          <div className="categories-bento">
            {categoryCards.map((card, index) => (
              <div
                key={card.target}
                className={`category-card category-card--${index + 1}`}
                role="button"
                tabIndex={0}
                onClick={() => scrollToSection(card.target)}
                onKeyDown={(event) => handleCardKeyDown(event, card.target)}
              >
                <img src={card.image} alt="" className="category-card-bg" loading="lazy" />
                <div className="category-card-content">
                  <h3>{card.title}</h3>
                  <p>{card.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {sections.map((section) => renderProductSection(section))}
    </div>
  );
};

export default Home;
